import { useState, useRef, useCallback, useEffect } from "react";

const CONFLICT_MESSAGE = "Email is already associated with a ONEpark user";
const GENERIC_ERROR_MESSAGE = "Failed to change the ONEpark email. Please try again.";

const messageForError = (error) => {
    const status = error?.response?.status;
    if (status === 409) {
        return CONFLICT_MESSAGE;
    }
    return GENERIC_ERROR_MESSAGE;
};

const useOneParkEmailChange = (changeEmail) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // A ref mirrors isSubmitting so the duplicate-submit guard is synchronous and
    // not subject to a stale closure between rapid activations (Req 9.2).
    const inFlightRef = useRef(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const clearError = useCallback(() => setErrorMessage(""), []);

    const submit = useCallback(
        async (newEmail) => {
            // Ignore duplicate submits while a request is in flight (Req 9.2).
            if (inFlightRef.current) return false;

            inFlightRef.current = true;
            setIsSubmitting(true);
            setErrorMessage("");

            try {
                await changeEmail(newEmail);
                return true;
            } catch (error) {
                if (mountedRef.current) {
                    setErrorMessage(messageForError(error));
                }
                return false;
            } finally {
                inFlightRef.current = false;
                if (mountedRef.current) {
                    // Stop the spinner and re-enable the submit control.
                    setIsSubmitting(false);
                }
            }
        },
        [changeEmail]
    );

    return { isSubmitting, errorMessage, submit, clearError };
};

export default useOneParkEmailChange;
export { CONFLICT_MESSAGE, GENERIC_ERROR_MESSAGE };
